import { Routes, Route } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Spinner, makeStyles } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import AppLayout from './components/layout/AppLayout';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import ClientPage from './pages/ClientPage';
import BankTransactionsPage from './pages/BankTransactionsPage';
import AccountTransactionsPage from './pages/AccountTransactionsPage';
import GlMappingPage from './pages/GlMappingPage';
import JournalPage from './pages/JournalPage';
import PatchNotesPage from './pages/PatchNotesPage';
import NotFoundPage from './pages/NotFoundPage';
import ExtractionAccuracyPage from './pages/ExtractionAccuracyPage';
import DocumentViewPage from './pages/DocumentViewPage';

const useStyles = makeStyles({
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
});

// Dev-only escape hatch: set VITE_AUTH_BYPASS=true in .env.local to skip the MSAL
// auth gate so the UI can be previewed before the Azure AD app registration exists.
// Backend endpoints are AuthorizationLevel.Anonymous, so requests go through without
// a Bearer token. Never set this in a production env.
const AUTH_BYPASS = import.meta.env.VITE_AUTH_BYPASS === 'true';

export default function App() {
  const styles = useStyles();
  const { t } = useTranslation();
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  if (!AUTH_BYPASS && inProgress !== InteractionStatus.None) {
    return (
      <div className={styles.loadingWrapper}>
        <Spinner size="large" label={t('app.signingIn')} />
      </div>
    );
  }

  if (!AUTH_BYPASS && !isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        {/* §9: one client context per page, carried in `?clientId=` so the screen is
            linkable and survives a refresh. */}
        <Route path="/clients" element={<ClientPage />} />
        {/* §9's "one flow, one output": the import file and the register are separate
            destinations, because offering both on one row made choosing between two Xero
            formats a decision pushed onto a bookkeeper mid-task. */}
        <Route path="/bank-transactions" element={<BankTransactionsPage />} />
        <Route path="/account-transactions" element={<AccountTransactionsPage />} />
        <Route path="/gl-mappings" element={<GlMappingPage />} />
        <Route path="/journals" element={<JournalPage />} />
        <Route path="/uploads/view" element={<DocumentViewPage />} />
        {/* Bilingual release notes, one release per deploy (fleet i18n standard §6). */}
        <Route path="/whats-new" element={<PatchNotesPage />} />
        {/* Mission item 4: the extractor's grade, read-only. */}
        <Route path="/extraction-accuracy" element={<ExtractionAccuracyPage />} />
        {/*
          Every path no route matches, including the retired `/export` (DP-CNV-EXPORT-ROUTE, ruled 2026-10-26: its
          one-release redirect had long outlived §9's "one release", with no user to break). Without this an unknown
          path rendered an empty shell.
        */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AppLayout>
  );
}

